import { useState } from "react";
import { Star, Quote } from "lucide-react";
import { useTestimonials } from "@/hooks/useTestimonials";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export function Testimonials() {
  const { testimonials, isLoading, getTestimonialsByRole, getFeaturedTestimonials } = useTestimonials(true); // only published
  const [activeTab, setActiveTab] = useState<'all' | 'parent' | 'athlete' | 'coach'>('all');
  const getFilteredTestimonials = () => {
    if (activeTab === 'all') {
      return testimonials || [];
    }
    return getTestimonialsByRole(activeTab);
  };

  const renderStars = (rating?: number) => {
    if (!rating) return null;
    return (
      <div className="flex items-center space-x-1 mt-2">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading testimonials...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-6">
            SUCCESS STORIES
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Real athletes. Real results. Real dreams achieved through RecruitLook's proven recruiting process.
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              variant={activeTab === 'all' ? 'default' : 'outline'}
              onClick={() => setActiveTab('all')}
              className="px-6 py-2"
            >
              All Stories
            </Button>
            <Button
              variant={activeTab === 'athlete' ? 'default' : 'outline'}
              onClick={() => setActiveTab('athlete')}
              className="px-6 py-2"
            >
              Athletes
            </Button>
            <Button
              variant={activeTab === 'parent' ? 'default' : 'outline'}
              onClick={() => setActiveTab('parent')}
              className="px-6 py-2"
            >
              Parents
            </Button>
            <Button
              variant={activeTab === 'coach' ? 'default' : 'outline'}
              onClick={() => setActiveTab('coach')}
              className="px-6 py-2"
            >
              Coaches
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-slate-900 mb-6">
              {activeTab === 'all' && 'SUCCESS STORIES'}
              {activeTab === 'athlete' && 'ATHLETE SUCCESS STORIES'}
              {activeTab === 'parent' && 'PARENT TESTIMONIALS'}
              {activeTab === 'coach' && 'COACH ENDORSEMENTS'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {activeTab === 'all' && 'Real stories from athletes, parents, and coaches who achieved their goals with RecruitLook.'}
              {activeTab === 'athlete' && 'From high school dreams to college commitments - hear directly from the athletes who achieved their goals with RecruitLook.'}
              {activeTab === 'parent' && 'The recruiting journey is a family commitment. Hear from parents who trusted RecruitLook to guide their athlete to success.'}
              {activeTab === 'coach' && 'College coaches trust RecruitLook to connect them with quality student-athletes. Here\'s what they\'re saying.'}
            </p>
          </div>

          {getFilteredTestimonials().length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-gray-500">
                No testimonials available{activeTab !== 'all' && ` for ${activeTab}s`} at this time.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {getFilteredTestimonials().map((testimonial) => (
                <div 
                  key={testimonial.id} 
                  className={`border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow ${
                    testimonial.role === 'coach' ? 'bg-slate-900 text-white' : 'bg-white'
                  } ${testimonial.featured ? 'ring-2 ring-secondary ring-opacity-50' : ''}`}
                >
                  <div className="flex items-start space-x-4">
                    <Quote className={`w-8 h-8 flex-shrink-0 mt-1 ${
                      testimonial.role === 'coach' ? 'text-secondary' : 'text-secondary'
                    }`} />
                    <div className="flex-1">
                      {testimonial.featured && (
                        <Badge className="mb-4 bg-secondary text-slate-900">Featured</Badge>
                      )}
                      
                      <blockquote className={`text-lg leading-relaxed mb-6 ${
                        testimonial.role === 'coach' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        "{testimonial.content}"
                      </blockquote>
                      
                      <div className={`border-t pt-6 ${
                        testimonial.role === 'coach' ? 'border-gray-700' : 'border-gray-100'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className={`font-black text-lg ${
                              testimonial.role === 'coach' ? 'text-secondary' : 'text-slate-900'
                            }`}>
                              {testimonial.name}
                            </div>
                            {testimonial.title && (
                              <div className={`text-sm mt-1 ${
                                testimonial.role === 'coach' ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {testimonial.title}
                              </div>
                            )}
                            <Badge variant="outline" className="mt-2 capitalize">
                              {testimonial.role}
                            </Badge>
                          </div>
                          <div className="text-right">
                            {renderStars(testimonial.rating)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-6">
            YOUR SUCCESS STORY STARTS HERE
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Join thousands of student-athletes who have achieved their college dreams through RecruitLook's proven recruiting process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-secondary text-slate-900 px-8 py-4 rounded-lg font-black text-lg hover:bg-secondary/90 transition-colors">
              START YOUR JOURNEY
            </button>
            <button className="border-2 border-secondary text-secondary px-8 py-4 rounded-lg font-black text-lg hover:bg-secondary hover:text-slate-900 transition-colors">
              SCHEDULE CONSULTATION
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
